import { useMemo } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import { THourRange, TWorkSchedule, isWorkingHour } from '../helpers/workSchedule';
import { TDateRangeSelection } from '../hooks/useDateRangeSelection';
import { HourCell, THourCellState } from './hourCell';

import style from '../dateRangeCalendar.module.scss';

type TProps = {
    /** Понедельник показываемой недели */
    weekStart: moment.Moment;
    hours: THourRange;
    schedule?: TWorkSchedule | null;
    selection: TDateRangeSelection;
    now: moment.Moment;
    onCellClick: (cell: moment.Moment) => void;
};

const getCellState = (
    cell: moment.Moment,
    { start, end }: TDateRangeSelection,
    schedule: TWorkSchedule | null | undefined,
    now: moment.Moment
): THourCellState => {
    const edges = (['start', 'end'] as const).filter((edge) =>
        Boolean({ start, end }[edge]?.isSame(cell, 'hour'))
    );
    const cellEnd = cell.clone().add(1, 'hour');
    // Ячейка в диапазоне, если её час пересекается с отрезком [начало; конец]
    const isInRange = Boolean(start && end && cellEnd.isAfter(start) && !cell.isAfter(end));

    return {
        isWorking: isWorkingHour(schedule, cell),
        isInRange,
        edges,
        isNow: now.isSame(cell, 'hour'),
    };
};

const getCellLabel = (cell: moment.Moment, { start, end }: TDateRangeSelection) => {
    const edge = [start, end].find((date) => date?.isSame(cell, 'hour'));
    return (edge ?? cell).format('HH:mm');
};

export const CalendarGrid = ({ weekStart, hours, schedule, selection, now, onCellClick }: TProps) => {
    const days = useMemo(
        () => Array.from({ length: 7 }, (_, index) => weekStart.clone().add(index, 'day')),
        [weekStart]
    );
    const hourList = Array.from({ length: hours.to - hours.from }, (_, index) => hours.from + index);

    return (
        <div className={style['grid']}>
            {days.map((day) => (
                <div
                    key={`head-${day.format('YYYY-MM-DD')}`}
                    className={classNames(style['day-head'], {
                        [style['day-head--today']]: day.isSame(now, 'day'),
                    })}
                >
                    <span>{day.locale('ru').format('dd')}</span>
                    <b>{day.format('DD')}</b>
                </div>
            ))}

            {hourList.map((hour) =>
                days.map((day) => {
                    const cell = day.clone().hour(hour).startOf('hour');
                    return (
                        <HourCell
                            key={cell.format('YYYY-MM-DD HH')}
                            label={getCellLabel(cell, selection)}
                            onClick={() => onCellClick(cell)}
                            {...getCellState(cell, selection, schedule, now)}
                        />
                    );
                })
            )}
        </div>
    );
};
